<template>
  <div
    v-if="data?.src"
    v-show="shouldShowEmbeddable(isEditMode, currentEmbedLocation, data?.headless_locations)"
    ref="embeddableContainer"
    class="embeddable__raw rounded-2 overflow-hidden"
    :class="{
      'embeddable__raw--fullscreen': isFullScreenMode,
      relative: !isFullScreenMode,
    }"
    :style="embeddableContainerStyle"
  >
    <div v-if="isComponentMounted" :style="iFrameHeight">
      <iframe
        ref="embeddableIframe"
        v-element-visibility="onAppVisibilityChange"
        class="w-full h-full border-none rounded-2"
        :class="{ 'embeddable__iframe--fullscreen': isFullScreenMode }"
        :data-app="props.data?.app_name"
        :src="embeddableSrc"
        title="embeddable"
      />
    </div>
    <div
      v-if="
        (mode === 'impact' || mode === 'sharing') &&
        data?.properties_by_embed_location?.[currentEmbedLocation]?.start_in_loading_mode &&
        (!embeddableIframe?.contentWindow || !loadedIframes?.has(embeddableIframe.contentWindow))
      "
      class="absolute top-0 bottom-0 left-0 right-0 z-1 flex items-center justify-center"
      :style="{ background: themeVars.primary6 }"
      @click="
        () =>
          isEditMode && (!componentSelectedMode || componentSelectedMode.id !== id) && emit('iframe-wrapper-clicked')
      "
    >
      <NSpin :color="themeVars.primary" size="large" />
    </div>
    <div
      v-else-if="isEditMode && (!componentSelectedMode || componentSelectedMode.id !== id)"
      class="absolute top-0 left-0 right-0 bottom-0"
      @click="emit('iframe-wrapper-clicked')"
    />
  </div>
</template>

<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { computed, onBeforeUnmount, onMounted, ref, toRef, toRefs, watch, inject, nextTick, type Ref } from 'vue'
import { syncRefs } from '@vueuse/core'
import { NSpin } from 'naive-ui'
import type pr from 'post-robot'
import { isEqual } from 'lodash-es'
import type { ComputedRef } from 'vue'

import { CanvasBuilderMode, type ComponentNode, type EmbeddableProps } from '@canvas-builder/types/canvas'
import { shouldShowEmbeddable } from '@canvas-builder/util/canvas.util'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import { CLIENT_TYPE, PLATFORM_TYPE } from '@lib/constants/cdp.const'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { deepToRaw } from '@lib/util/reactive'
import { IFRAME_ACTION_TYPES, IFRAME_DATA_MESSAGE, type IFrameMessage } from '@lib/types/iframes'
import { isOriginValid } from '@lib/util/message-event'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import { PRINT_SCALE_FACTOR } from '@lib/constants/config.const'
import type { UiAppSetDataPayload, UiAppUpdateDataPayload } from '@lib/sdk/main'
import { UI_API_METHOD_TYPES, UI_MESSAGE_TYPES } from '@lib/sdk/main'
import { useInstalledEmbeddables } from '@canvas-builder/composables/useInstalledEmbeddables'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { setupFullScreenMode } from '@canvas-builder/util/iframe.util'
import type { CanvasRetrieve } from '@openapi'
type CrossDomainWindowType = Parameters<typeof pr.send>[0]

const props = withDefaults(
  defineProps<{
    data?: EmbeddableProps
    id: string
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
  }>(),
  {
    usedInSectionId: '',
    when_used_in_section: () => defaultWhenUsedInSection,
  },
)

const emit = defineEmits<{
  (e: 'iframe-wrapper-clicked'): void
}>()

const loadedIframes = inject<Ref<Set<CrossDomainWindowType>>>(
  'loadedIframes',
  computed(() => new Set()),
)

const sectionListSectionInfo = inject<
  ComputedRef<{
    usedInSectionId: string
    sectionIdx: number
    sectionListId: string
    printModeIdx?: number
  } | null>
>(
  'sectionListSectionInfo',
  computed(() => null),
)

const { id } = toRefs(props)

const {
  getEnv,
  activeCanvas,
  activeCanvasSections,
  isEditMode,
  componentSelectedMode,
  mode,
  inCall,
  currentEmbedLocation,
  displayMode,
  isPrintDisplayMode,
  onAppEnter,
  onAppExit,
} = useCanvas()
const { shouldUseDeviceUrl, isFetching: isFetchingDeviceEmbeddables, getDeviceUrl } = useInstalledEmbeddables()
const { goToNextPage, goToPreviousPage, goToPage } = useCanvasPages()

const isAppVisible = ref(false)
const isComponentMounted = ref(false)
const propHeight = toRef(() => props.data?.height)
const iFramePrintHeight = ref<string | undefined>(props.data?.height)
syncRefs(propHeight, iFramePrintHeight)

// Check if full screen mode is enabled for this embeddable
const isFullScreenMode = computed(() => {
  return !isEditMode.value && props.data?.full_screen_mode === true
})

// Container style for the embeddable wrapper
const embeddableContainerStyle = computed(() => {
  if (isFullScreenMode.value) {
    // Use fixed positioning to break out of relative containers
    // and position relative to the canvas-builder-layout__content
    return {}
  }
  return {
    width: props.data?.width,
  }
})

const iFrameHeight = computed(() => {
  // In full screen mode, use 100% height
  if (isFullScreenMode.value) {
    return { height: '100%' }
  }

  return { height: getScaledHeight() + 'px' }
})

const getScaledHeight = () => {
  const value = iFramePrintHeight.value || props.data?.height

  if (!value) return 0

  const height = Number.parseInt(value)

  if (isNaN(height)) {
    return 0
  }

  if (isPrintDisplayMode.value) {
    return height * (1 / PRINT_SCALE_FACTOR)
  }

  return height
}

const themeVars = useThemeVars()
const embeddableIframe = ref<HTMLIFrameElement | null>(null)
const embeddableContainer = ref<HTMLElement | null>(null)

const embeddableSrc = computed(() => {
  try {
    const appName = props.data?.app_name
    if (appName && doesLocalOverrideExist(appName)) {
      return getLocalOverrideUrl(appName)
    }
  } catch (e) {
    console.error('Error generating Embeddable debug url', e)
  }

  if (shouldUseDeviceUrl.value) {
    if (isFetchingDeviceEmbeddables.value) return undefined

    return getDeviceUrl(props.data?.file?.id)
  }

  return props.data?.src || undefined
})

const client = computed(() =>
  mode.value
    ? {
        [CanvasBuilderMode.ADMIN]: CLIENT_TYPE.admin,
        [CanvasBuilderMode.IMPACT]: CLIENT_TYPE.impact,
        [CanvasBuilderMode.SHARING]: CLIENT_TYPE.dsr,
        [CanvasBuilderMode.HOME]: CLIENT_TYPE.impactCanvasHome,
      }[mode.value]
    : null,
)

watch([inCall, isEditMode], ([inCall, isEditMode]) => {
  if (embeddableIframe.value?.contentWindow) {
    const updateDataPayload: UiAppUpdateDataPayload = {
      in_call: inCall ?? false,
      is_edit_mode: isEditMode ?? false,
    }

    // TODO: Remove this once all embeddables are updated to use the new message type
    embeddableIframe.value?.contentWindow.postMessage(
      {
        type: IFRAME_DATA_MESSAGE,
        body: {
          action: IFRAME_ACTION_TYPES.UPDATE_DATA,
          data: updateDataPayload,
        },
      },
      '*',
    )

    embeddableIframe.value?.contentWindow.postMessage(
      {
        type: UI_MESSAGE_TYPES.UI_APP_UPDATE_DATA,
        body: updateDataPayload,
      },
      '*',
    )
  }
})

const updateIframeData = async () => {
  const envData = client.value === 'impact' ? await getEnv?.value?.() : { mode: 'WEB', pitcher: { user: null } }
  const user = envData?.pitcher?.user ? deepToRaw(envData.pitcher.user) : null
  const usedInSection = deepToRaw(activeCanvasSections.value?.[props.usedInSectionId])
  const canvasWithoutSectionsContent = deepToRaw({
    ...activeCanvas.value,
    sections: activeCanvas.value?.sections.map(({ content, ...s }) => s) ?? [],
  })
  const idxForSection = isPrintDisplayMode.value
    ? (sectionListSectionInfo.value?.printModeIdx ?? null)
    : (sectionListSectionInfo.value?.sectionIdx ?? null)
  const setDataPayload: UiAppSetDataPayload = {
    client: client.value,
    platform: envData
      ? {
          IOS: PLATFORM_TYPE.ios,
          WEB: PLATFORM_TYPE.web,
          ANDROID: PLATFORM_TYPE.android,
          WINDOWS: PLATFORM_TYPE.windows,
        }[envData.mode]
      : undefined,
    user,
    is_edit_mode: isEditMode.value ?? false,
    in_call: inCall.value ?? false,
    canvas: canvasWithoutSectionsContent as CanvasRetrieve,
    used_in_section: usedInSection,
    component_id: props.id,
    settings: deepToRaw(props.data?.settings) || null,
    width: props.data?.width,
    height: props.data?.height,
    full_screen_mode: props.data?.full_screen_mode,
    display_mode: displayMode.value,
    section_index: idxForSection ?? null,
    section_list_id: sectionListSectionInfo.value?.sectionListId ?? null,
    canvas_correlation_id: envData && 'state' in envData ? envData.state?.canvas_correlation_id : undefined,
  }

  // TODO: Remove this once all embeddables are updated to use the new message type
  embeddableIframe.value?.contentWindow?.postMessage(
    {
      type: IFRAME_DATA_MESSAGE,
      body: {
        action: IFRAME_ACTION_TYPES.SET_DATA,
        data: setDataPayload,
      },
    },
    '*',
  )

  embeddableIframe.value?.contentWindow?.postMessage(
    {
      type: UI_MESSAGE_TYPES.UI_APP_SET_DATA,
      body: setDataPayload,
    },
    '*',
  )
}

// Send new data to iframe when widget properties change
watch(
  () => props.data,
  (newData, oldData) => {
    if (newData && oldData && !isEqual(newData, oldData)) updateIframeData()
  },
)

function handleAppMethod(event: MessageEvent<any>) {
  if (!isOriginValid(event)) return
  if (event.source !== embeddableIframe.value?.contentWindow) return

  const { type, body } = event.data

  if (type === UI_API_METHOD_TYPES.UI_APP_RESIZE) {
    iFramePrintHeight.value = `${String(body?.height)}px`
  } else if (type === UI_API_METHOD_TYPES.UI_APP_LOADED) {
    updateIframeData()
  }
}

const onIframeMessage = (event: MessageEvent<IFrameMessage>) => {
  if (!isOriginValid(event)) return
  if (event.source !== embeddableIframe.value?.contentWindow) return

  const { type, body } = event.data

  if (type === IFRAME_DATA_MESSAGE) {
    switch (body?.action) {
      case IFRAME_ACTION_TYPES.RESIZE_IFRAME:
        iFramePrintHeight.value = `${String(body.data?.height)}px`
        break
      case IFRAME_ACTION_TYPES.IFRAME_LOADED:
        updateIframeData()
        break
      case IFRAME_ACTION_TYPES.NAVIGATE_NEXT_PAGE:
        goToNextPage()
        break
      case IFRAME_ACTION_TYPES.NAVIGATE_PREVIOUS_PAGE:
        goToPreviousPage()
        break
      case IFRAME_ACTION_TYPES.NAVIGATE_PAGE:
        if (body.data?.page) goToPage({ componentId: body.data?.page, scrollTo: 'top' })
        break
    }
  }
}

function cleanupIframe() {
  if (embeddableIframe.value) {
    embeddableIframe.value.src = 'about:blank'
    embeddableIframe.value = null
  }
}

const onAppVisibilityChange = (isVisible: boolean) => {
  isAppVisible.value = isVisible
}

let cleanupFullScreen: (() => void) | null = null

watch(
  isFullScreenMode,
  async (newValue) => {
    await nextTick()
    if (newValue && embeddableContainer.value) {
      cleanupFullScreen = setupFullScreenMode(embeddableContainer.value, () => isFullScreenMode.value)
    } else if (cleanupFullScreen) {
      cleanupFullScreen()
      cleanupFullScreen = null
    }
  },
  { immediate: true },
)

watch(
  [embeddableSrc, isAppVisible],
  async ([newSrc, newVisible], [oldSrc, oldVisible]) => {
    const wasActive = oldVisible && oldSrc
    const isActive = newVisible && newSrc
    const isDifferentSrc = newSrc === oldSrc

    if (wasActive && (!newVisible || (newVisible && isDifferentSrc))) {
      onAppExit.value?.({ src: oldSrc })
    }

    if (isActive && (!oldVisible || isDifferentSrc)) {
      await onAppEnter.value?.({ src: newSrc })
    }
  },
  { immediate: true },
)

onMounted(() => {
  updateIframeData()
  window.addEventListener('message', onIframeMessage)
  window.addEventListener('message', handleAppMethod)

  // set the component as mounted after setup to avoid any race conditions
  isComponentMounted.value = true
})

onBeforeUnmount(() => {
  window.removeEventListener('message', onIframeMessage)
  window.removeEventListener('message', handleAppMethod)
  cleanupIframe()
  cleanupFullScreen?.()
  if (embeddableSrc.value) {
    onAppExit.value?.({ src: embeddableSrc.value })
  }
})
</script>

<style scoped lang="scss">
.embeddable__raw--fullscreen {
  // All positioning is handled by JavaScript
  // Only keep styles that can't be set via JS or need !important to override
  border-radius: 0 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

.embeddable__iframe--fullscreen {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
  border-radius: 0 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
  max-width: 100% !important;
  max-height: 100% !important;
}
</style>
